// Unit conversions and display formatters.
//
// Storage is ALWAYS SI: meters, m/s, radians, kilograms. This module is
// the display layer — converting on read for UI fields and converting
// the user's typed/slider input back to SI before write.
//
// Formatters take `unitSystem` ('metric' | 'imperial') as a parameter
// rather than reading a closure-bound global, so each app owns its own
// unitSystem state. The user's preference is typically persisted in
// localStorage by the consuming app.

export const M_TO_FT  = 3.28084;
export const FT_TO_M  = 1 / M_TO_FT;
export const M_TO_MI  = 1 / 1609.344;
export const KMH_TO_MPH = 0.621371;
export const MPH_TO_KMH = 1 / KMH_TO_MPH;
export const MS_TO_MPH  = 2.23694;   // = 3.6 * KMH_TO_MPH
export const MS_TO_KMH  = 3.6;

// Format a meters value for a "short distance" UI field — track widths,
// wall thicknesses, curb widths. Always returns "{value} {unit}".
export function fmtShort(meters, unitSystem, decimals = 1) {
  if (unitSystem === 'imperial') return `${(meters * M_TO_FT).toFixed(decimals)} ft`;
  return `${meters.toFixed(decimals)} m`;
}

// Format a meters value for cursor / waypoint coordinate readouts. No
// unit suffix — coordinate rows show the unit once in the header.
export function fmtCoord(meters, unitSystem) {
  if (unitSystem === 'imperial') return (meters * M_TO_FT).toFixed(1);
  return meters.toFixed(1);
}

export function unitShort(unitSystem) {
  return unitSystem === 'imperial' ? 'ft' : 'm';
}

// Format a "long distance" — lap length, bounding-box dimensions. Picks
// miles when ≥ 0.5 mi (typical circuit), feet otherwise. Metric uses km
// when ≥ 1 km, meters otherwise.
export function fmtLong(meters, unitSystem) {
  if (unitSystem === 'imperial') {
    const miles = meters * M_TO_MI;
    if (miles >= 0.5) return `${miles.toFixed(2)} mi`;
    return `${Math.round(meters * M_TO_FT)} ft`;
  }
  if (meters >= 1000) return `${(meters / 1000).toFixed(2)} km`;
  return `${Math.round(meters)} m`;
}

// Format a speed in m/s for display. Imperial → mph, metric → km/h.
export function fmtSpeed(metersPerSec, unitSystem, decimals = 0) {
  const v = unitSystem === 'imperial' ? metersPerSec * MS_TO_MPH : metersPerSec * MS_TO_KMH;
  const unit = unitSystem === 'imperial' ? 'mph' : 'km/h';
  return `${v.toFixed(decimals)} ${unit}`;
}
